import { PlayerChartConfig, PlayerChartProvider } from '@/components/General/PlayerChart';
import { PlayerChart, TimelinePlayer } from '@/components/General/PlayerChart/components';
import { Drawer, Spin } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { transformReplayDataToChartDataWithTimestamp } from '@/utils/replayInteractiveChartTransform';
import { transformReplayDataToRequestEvents } from '@/utils/requestEventsTransform';
import InteractiveChartHeader from './InteractiveChartHeader';
import InteractiveEmptyState from './InteractiveEmptyState';
import {
	DateInput,
	Parameter,
	ReplayInteractiveData,
	Iteration,
} from '@/pages/InteractiveSimulation/types/replayInteractive';
import { useHistoricalDataFetcher } from '@/hooks/InteractiveSimulation';
import { MeasurementInfrastructure } from '@/domain/InteractiveSimulations/MeasurementInfrastructure';

interface ReplayInteractiveContentChartPlayerProps {
	title: string;
	chartStartDate: DateInput;
	chartEndDate: DateInput;
	parameters: Parameter[];
	iterations: Iteration[];
	currentIteration: Iteration | null;
	dataReplayInteractive: ReplayInteractiveData[];
	infraestructures: MeasurementInfrastructure[];
}

function ReplayInteractiveContentChartPlayer(props: ReplayInteractiveContentChartPlayerProps) {
	const instanceId = 'replay-interactive-chart-player';
	const [switchEnabled, setSwitchEnabled] = useState(false);

	const { historicalData, isLoadingHistoricalData, fetchHistoricalData, clearHistoricalData } =
		useHistoricalDataFetcher({
			parameters: props.parameters,
			chartStartDate: props.chartStartDate,
			chartEndDate: props.chartEndDate,
		});

	const handleSwitchChange = (checked: boolean) => {
		setSwitchEnabled(checked);

		if (checked) {
			void fetchHistoricalData();
		} else {
			clearHistoricalData();
		}
	};

	const startDate = dayjs(props.chartStartDate).isValid()
		? dayjs(props.chartStartDate).toDate()
		: null;
	const endDate = dayjs(props.chartEndDate).isValid() ? dayjs(props.chartEndDate).toDate() : null;

	if (!startDate || !endDate) {
		return null;
	}

	const chartData = transformReplayDataToChartDataWithTimestamp(
		props.dataReplayInteractive,
		props.parameters,
	);

	const requestEvents = transformReplayDataToRequestEvents(props.dataReplayInteractive, props.infraestructures);

	const playerChartConfig: PlayerChartConfig = {
		chartData: chartData,
		historicalData: historicalData,
		playerSpeed: 500,
		dateRange: {
			startDate: startDate,
			endDate: endDate,
		},
		requestEvents: requestEvents,
	};

	return (
		<PlayerChartProvider config={playerChartConfig} instanceId={instanceId}>
			<Drawer
				title={
					<div style={{ position: 'relative' }}>
						<InteractiveChartHeader
							mode="replay"
							title={props.title}
							parameters={props.parameters}
							iterations={props.iterations}
							currentIteration={props.currentIteration}
							switchEnabled={switchEnabled}
							onSwitchChange={handleSwitchChange}
						/>
						{switchEnabled && isLoadingHistoricalData && (
							<div
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									backgroundColor: 'rgba(255, 255, 255, 0.7)',
									zIndex: 1002,
									pointerEvents: 'all',
								}}
							/>
						)}
					</div>
				}
				placement="bottom"
				onClose={() => {}}
				open={true}
				height="auto"
				styles={{ 
					body: { padding: 0, overflow: 'visible' },
					wrapper: { overflow: 'visible' }
				}}
				maskClosable={false}
				closable={false}
				mask={false}
				zIndex={1000}
			>
				<Spin
					spinning={switchEnabled && isLoadingHistoricalData}
					tip="Loading Historical Data..."
					className="w-full min-h-[200px]"
					style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1001 }}
				>
					<div className="w-full flex flex-col p-4">
						<div className="w-full pt-2" style={{ overflow: 'visible' }}>
							{props.parameters?.length === 0 ? (
								<InteractiveEmptyState />
							) : (
								<>
									<TimelinePlayer />
									<PlayerChart highChartHeight={160} />
								</>
							)}
						</div>
					</div>
				</Spin>
			</Drawer>
		</PlayerChartProvider>
	);
}

export default ReplayInteractiveContentChartPlayer;
